// MicroformClient.ts - FIXED VERSION

let microformInstance: any = null;
let microformInitialized = false;
let fieldsLoaded = false;  // ← NEW: track if fields have been loaded

const NUMBER_CONTAINER_ID = 'microform-number-container';
const CVV_CONTAINER_ID = 'microform-securityCode-container';

const cleanupMicroform = () => {
  if (microformInstance) {
    try {
      if (typeof microformInstance.destroy === 'function') {
        microformInstance.destroy();
      }
    } catch (e) { /* ignore */ }
    microformInstance = null;
  }
  microformInitialized = false;
  fieldsLoaded = false;  // ← Reset load guard

  [NUMBER_CONTAINER_ID, CVV_CONTAINER_ID].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.innerHTML = '';
  });
};

const createMicroformInstance = (captureContext: string) => {
  if (microformInitialized && fieldsLoaded) {
    console.log('Microform already initialized and fields loaded, skipping');
    return;
  } else {
    console.log('Creating Microform instance');
  }

  // @ts-ignore
  const Flex = (window as any).Flex;
  if (!Flex) throw new Error('Flex SDK not loaded');

  cleanupMicroform(); // Always clean first

  microformInstance = new Flex(captureContext).microform({
    styles: {
      input: {
        'font-size': '13px',
        'font-family': 'Roboto, Helvetica, Arial, sans-serif',
        'font-weight': '500',
        'color': 'rgba(0,0,0,0.87)',
        'padding': '4px 0 5px 0',
        'line-height': '1.4375em',
        'border': 'none',
        'border-bottom': '1px solid rgba(0,0,0,0.42)',
      },
      '::placeholder': { color: '#999' },
    },
  });

  const number = microformInstance.createField('number', { placeholder: 'Card Number' });
  const securityCode = microformInstance.createField('securityCode', { placeholder: 'CVV' });
  const numberEl = document.getElementById(NUMBER_CONTAINER_ID);
  const cvvEl = document.getElementById(CVV_CONTAINER_ID);

  if (!numberEl || !cvvEl) {
    console.error('Microform containers missing');
    return;
  }

  // Only load once
  if (!fieldsLoaded) {
    number.load('#' + NUMBER_CONTAINER_ID);
    securityCode.load('#' + CVV_CONTAINER_ID);
    fieldsLoaded = true;
    console.log('Microform fields loaded (once)');
  }

  microformInitialized = true;
};

export const initializeMicroform = (captureContext: string): Promise<void> => {
  if (!captureContext) {
    cleanupMicroform();
    return Promise.resolve();
  }

  // If already initialized with the same captureContext, do nothing
  if (microformInitialized && microformInstance) {
    console.log('Microform already initialized, skipping');
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    try {
      const payload = JSON.parse(atob(captureContext.split('.')[1]));
      const clientLibrary = payload.ctx?.[0]?.data?.clientLibrary;
      if (!clientLibrary) throw new Error('clientLibrary missing');

      // If SDK is already loaded, just create instance
      // @ts-ignore
      if ((window as any).Flex) {
        createMicroformInstance(captureContext);
        resolve();
        return;
      }

      // Prevent duplicate script
      const existingScript = document.querySelector(`script[src="${clientLibrary}"]`);
      if (existingScript) {
        // Script tag exists but Flex not on window yet — wait for it
        const checkFlex = () => {
          // @ts-ignore
          if ((window as any).Flex) {
            createMicroformInstance(captureContext);
            resolve();
          } else {
            setTimeout(checkFlex, 50);
          }
        };
        checkFlex();
        return;
      }

      const script = document.createElement('script');
      script.src = clientLibrary;
      script.async = true;
      const integrity = payload.ctx?.[0]?.data?.clientLibraryIntegrity;
      if (integrity) {
        script.integrity = integrity;
        script.crossOrigin = 'anonymous';
      }

      script.onload = () => {
        createMicroformInstance(captureContext);
        resolve();
      };

      script.onerror = () => reject(new Error('Failed to load Flex SDK'));

      document.head.appendChild(script);
    } catch (err) {
      reject(err);
    }
  });
};

export const createMicroformToken = (expMonth: string, expYear: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!microformInitialized || !microformInstance) {
      reject(new Error('Microform not ready'));
      return;
    }

    microformInstance.createToken(
      { expirationMonth: expMonth.padStart(2, '0'), expirationYear: expYear },
      (err: any, token: string) => {
        if (err) reject(err);
        else resolve(token);
      }
    );
  });
};

export const resetMicroform = cleanupMicroform;